// src/components/pages/message/timecapsule/Step3Review.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CardMedia, LinearProgress } from "@mui/material";
import ActionButton from "../../../atoms/ActionButton";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import IconTimeCapsule from "../../../../assets/icons/icon-time-capsule-unlocked.png";

// Firebase
import { db, auth, storage } from "../../../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// Geohash
import ngeohash from "ngeohash";

const Step3Review = ({ wizardData, onBack }) => {
  const navigate = useNavigate();

  const {
    lat,
    lng,
    address,
    subject,
    message,
    mediaFile,       // Expect { file: File, url: blobUrl, type: string }
    recipients = [],
    phoneNumbers = [],
    revealDate,
  } = wizardData;

  const formattedRevealDate = revealDate
    ? new Date(revealDate).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : "";

  // SMS fallback recipients
  const smsRecipientsArray = [
    ...phoneNumbers.map((p) => (typeof p === "object" ? p.phone : p)),
    ...recipients.map((r) => (typeof r === "object" ? r.phone : r)),
  ].filter(Boolean);

  // Share text + URL
  const shareText = `I’ve dropped a time capsule at ${address}${formattedRevealDate ? `, unlocking on ${formattedRevealDate}` : ""}.\n\n"${message}"`;
  const shareUrl = 'https://nflight.staging.kbean.com/messages';

  // Local UI state
  const [isSending, setIsSending] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSend = async () => {
    // 1️⃣ Native share dialog when available, otherwise SMS fallback
    // if (navigator.share) {
    //   try {
    //     await navigator.share({
    //       title: "Your Time Capsule",
    //       text: shareText,
    //       url: shareUrl,
    //     });
    //   } catch (err) {
    //     console.warn("Share failed:", err);
    //   }
    // } else {
    //   // SMS fallback: use the right delimiter and body syntax per platform
    //   const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    //   const delimiter = isIOS ? ";" : ",";
    //   const recipients = smsRecipientsArray.join(delimiter);
    //   const bodyParam =
    //     (isIOS ? "&" : "?") +
    //     "body=" +
    //     encodeURIComponent(`${shareText}\n\nView: ${shareUrl}`);
    //   window.location.href = `sms:${recipients}${bodyParam}`;
    // }

    // SMS fallback: use the right delimiter and body syntax per platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // const delimiter = isIOS ? ";" : ",";
    // const recipients = smsRecipientsArray.join(delimiter);
    const recipients = smsRecipientsArray.join(',');  

    const encodedBody = encodeURIComponent(`${shareText}\n\nView: ${shareUrl}`);

    if (isIOS) {
      // Undocumented but works on many iOS versions:
      window.location.href = `sms:/open?addresses=${recipients}&body=${encodedBody}`;
    } else {
      // Standard scheme works on Android and most other platforms:
      window.location.href = `sms:${recipients}?body=${encodedBody}`;
    }

    // 2️⃣ Write to Firestore only once
    if (!hasSaved) {
      setIsSending(true);

      // 2a) Upload media if present
      let mediaEntry = null;
      if (mediaFile && mediaFile.file) {
        try {
          const path = `timecapsules/${Date.now()}-${mediaFile.file.name}`;
          const storageRef = ref(storage, path);
          const task = uploadBytesResumable(storageRef, mediaFile.file);

          task.on(
            "state_changed",
            (snapshot) => {
              const pct = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setUploadProgress(pct);
            },
            (error) => {
              console.error("Upload error:", error);
              throw error;
            }
          );

          await task;
          const downloadURL = await getDownloadURL(storageRef);
          mediaEntry = {
            type: mediaFile.type.startsWith("video") ? "video" : "image",
            url: downloadURL,
          };
        } catch (err) {
          console.error("Failed to upload media:", err);
          alert("Error uploading media. Please try again.");
          setIsSending(false);
          return;
        }
      }

      // 2b) Compute geohash
      const geohash = ngeohash.encode(lat, lng);

      // 2c) Write document
      let docRef;
      try {
        docRef = await addDoc(collection(db, "timeCapsules"), {
          senderId: auth.currentUser.uid,
          location: { address, lat, lng },
          geohash,
          media: mediaEntry ? [mediaEntry] : [],
          messageTitle: subject,
          messageBody: message,
          revealDate: revealDate ? new Date(revealDate) : null,
          recipientPhoneNumbers: smsRecipientsArray,
          capsuleType: "private",
          createdAt: serverTimestamp(),
          sentAt: serverTimestamp(),
          status: "sent",
        });
        setHasSaved(true);
      } catch (err) {
        console.error("Error saving time capsule:", err);
        alert("Error saving to database. Please try again.");
        setIsSending(false);
        return;
      } finally {
        setIsSending(false);
      }

      // 3️⃣ Navigate to the new capsule detail page
      navigate(`/capsule/${docRef.id}`);
    }
  };

  // Helper to display recipient list
  const displayRecipients = () => {
    const manual = phoneNumbers.map((p) =>
      p && typeof p === "object"
        ? p.name
          ? `${p.name} (${p.phone})`
          : p.phone
        : p
    );
    const contactList = recipients.map((r) =>
      r && typeof r === "object"
        ? r.name
          ? `${r.name} (${r.phone})`
          : r.phone
        : r
    );
    const combined = [...manual, ...contactList].filter(Boolean);
    return combined.length
      ? combined.join(", ")
      : "No recipients selected";
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 3: Review & Confirm
      </Typography>
      <Typography variant="body1" gutterBottom>
        Almost there! Confirm everything and tap Send.
      </Typography>

      {/* Media Preview */}
      {mediaFile && (
        <Box sx={{ mb: 4, mt: 3, position: "relative" }}>
          {mediaFile.type.startsWith("image/") ? (
            <CardMedia
              component="img"
              image={mediaFile.url}
              alt="preview"
              sx={{ width: "100%", height: "auto", borderRadius: "20px" }}
            />
          ) : (
            mediaFile.type.startsWith("video/") && (
              <CardMedia
                component="video"
                src={mediaFile.url}
                controls
                sx={{ maxHeight: 200 }}
              />
            )
          )}
          {isSending && uploadProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
            />
          )}
        </Box>
      )}

      {/* Location */}
      <Typography sx={{ mb: 4, fontWeight: 700 }}>{address}</Typography>

      {/* Recipients */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 4 }}>
        <Box
          sx={(theme) => ({
            borderRadius: "50px",
            backgroundColor: theme.colors.blue,
            p: 1,
            pl: 2,
            pr: 2,
            alignSelf: "flex-start",
          })}
        >
          <Typography sx={(theme) => ({ color: theme.colors.white })}>
            My contacts
          </Typography>
        </Box>
        <Typography
          sx={(theme) => ({
            color: theme.colors.blue,
            fontWeight: 700,
          })}
        >
          {displayRecipients()}
        </Typography>
      </Box>

      {/* Subject */}
      <Typography sx={{ mb: 1 }}>
        <strong>Subject:</strong> {subject}
      </Typography>

      {/* Message */}
      <Typography sx={{ mb: 4 }}>
        <strong>Message:</strong> {message}
      </Typography>

      {/* Drop & Reveal */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <img
          src={IconTimeCapsule}
          alt="Time Capsule"
          style={{ width: "auto", height: "80px" }}
        />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Reveals instantly if recipients are nearby
          {formattedRevealDate &&
            ` and unlocks on ${formattedRevealDate}`}
        </Typography>
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 10,
        }}
      >
        {!isSending && (
          <ActionButton
            text="< Back"
            onClick={onBack}
            sx={(theme) => ({
              backgroundColor: theme.colors.white,
              color: "black",
              textDecoration: "underline",
              borderRadius: "50px",
              padding: "14px 0px",
              marginBottom: "10px",
              fontWeight: "bold",
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            })}
          />
        )}
        <ActionButton
          icon={IconNextArrow}
          iconWidth={15}
          iconHeight={15}
          text={isSending ? "Sending..." : "Send it!"}
          onClick={handleSend}
          disabled={isSending}
          sx={(theme) => ({
            backgroundColor: theme.colors.blue,
            color: theme.colors.white,
            borderRadius: "50px",
            padding: "14px 30px",
            marginBottom: "10px",
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
      </Box>
    </Box>
  );
};

export default Step3Review;
